import { Icon, MUIButton, MUITextField, MUITooltip } from "@abb/arcadia-ui-react"
import { StatusCodes } from "http-status-codes"
import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../services/api"
import { FactoryTypeEnum } from "../../../models/Factory"
import {
  FactoryReportGet,
  FactoryReportSearchDetailGet,
  FactoryReportSearchPost,
} from "../../../models/FactoryReport"
import { EntityTab } from "../../partials/EntityTab"

const SEARCH_PLACEHOLDER =
  "Paste Factory Report serial numbers separated by spaces, commas, semicolons, or new lines"

const REPORT_COLUMN_WIDTH = "120px"

function ReportColumnHeader({
  abbreviation,
  reportName,
}: Readonly<{
  abbreviation: string
  reportName: string
}>) {
  return (
    <MUITooltip
      tooltipText={reportName}
      position="top"
      parentClassName="factory-report-column-header-tooltip"
    >
      <span className="factory-report-column-header" aria-label={reportName}>
        {abbreviation}
      </span>
    </MUITooltip>
  )
}

export const checkSerialNumber = (serialNumber: string): boolean => {
  const lowVoltageCircuitBreakerSerialNumberRegex =
    /^(?<ProductionYear>[A-HL-M]{2})(?<ProductionMonth>[1-9A-C])(?<ProductionSite>[\dA-C])(?<SerialNumber>\d{6})$/

  const softstarterSerialNumberRegex =
    /^(?:(?<PrefixVasteras>1S)(?<RegisterVasteras>160)|(?<PrefixXinhui>2T)(?<RegisterXinhui>110))(?<ProductType>[\dA-HJ-NPR-Z]{4})(?<ProductionYear>\d{2})(?<ProductionWeek>[0-5]\d)(?<SerialNumber>[\dA-HJ-NPR-Z]{4})$/

  const truOneSerialNumberRegex =
    /^(?<Prefix>1S)(?<Register>200)(?<ProductFamily>C)(?<ProductionSite>1)(?<ProductionYear>\d{2})(?<ProductionWeek>[0-5]\d)(?<ProductionDayOfWeek>[1-7])(?<SerialNumber>\d{5})$/

  return (
    lowVoltageCircuitBreakerSerialNumberRegex.test(serialNumber) ||
    softstarterSerialNumberRegex.test(serialNumber) ||
    truOneSerialNumberRegex.test(serialNumber)
  )
}

export const parseSerialNumbers = (input: string): string[] => {
  const serialNumbers = input
    .split(/[\s,;]+/)
    .map((serialNumber) => serialNumber.trim().toUpperCase())
    .filter((serialNumber) => serialNumber.length > 0)
    .filter(checkSerialNumber)

  return Array.from(new Set(serialNumbers))
}

const downloadReport = (downloadLink: string) => {
  const anchor = document.createElement("a")
  anchor.href = downloadLink
  anchor.download = ""
  anchor.rel = "noopener"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

const getReportByType = (
  factoryReport: FactoryReportGet,
  reportType: keyof typeof FactoryTypeEnum
) => factoryReport.reports?.find((report) => report.type === reportType)

const getReportSortValue = (report?: FactoryReportSearchDetailGet) => {
  if (!report?.downloadLink) {
    return "0"
  }

  return report.isAvailable ? "2" : "1"
}

function ReportDownloadButton({
  report,
  reportName,
}: Readonly<{
  report?: FactoryReportSearchDetailGet
  reportName: string
  text: string
}>) {
  const downloadLink = report?.downloadLink

  const handleDownload = useCallback(() => {
    if (downloadLink) {
      downloadReport(downloadLink)
    }
  }, [downloadLink])

  if (!downloadLink) {
    return null
  }

  return (
    <button
      type="button"
      className={`factory-report-download ${
        report.isAvailable
          ? "factory-report-download--available"
          : "factory-report-download--unavailable"
      }`}
      aria-label={`Download ${reportName}`}
      title={`Download ${reportName}`}
      onClick={handleDownload}
    >
      <Icon iconName="icon_abb_dias icon-abb_download" size={24} disableHover />
    </button>
  )
}

export function FactoryReportSearch() {
  const [searchText, setSearchText] = useState("")
  const [isRetrievingFactoryReports, setIsRetrievingFactoryReports] = useState(false)
  const [factoryReports, setFactoryReports] = useState<FactoryReportGet[]>([])

  const serialNumbers = useMemo(() => parseSerialNumbers(searchText), [searchText])

  const retrieveFactoryReports = useCallback(() => {
    if (serialNumbers.length === 0) {
      return
    }

    setIsRetrievingFactoryReports(true)

    const searchRequest: FactoryReportSearchPost[] = serialNumbers.map(
      (serialNumber) => ({ serialNumber })
    )

    digitalManagerApi
      .post<FactoryReportGet[]>(`/factoryReports/search`, searchRequest)
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          setFactoryReports(res.data)
        }
      })
      .catch(() => {
        toast.error("Cannot retrieve factory reports")
      })
      .finally(() => {
        setIsRetrievingFactoryReports(false)
      })
  }, [serialNumbers])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        retrieveFactoryReports()
      }
    },
    [retrieveFactoryReports]
  )

  const clearSearchText = useCallback(() => {
    setSearchText("")
  }, [])

  const columns = [
    {
      field: "serialNumber",
      headerName: "Serial Number",
    },
    {
      field: "partNumber",
      headerName: "PartNumber",
    },
    {
      field: "deviceName",
      headerName: "DeviceName",
    },
    {
      field: "status",
      headerName: "Status",
    },
    {
      field: "testReport",
      headerName: "Test Report",
      customHeader: (
        <ReportColumnHeader abbreviation="T.R." reportName="Test Report" />
      ),
      columnMinWidth: REPORT_COLUMN_WIDTH,
      columnMaxWidth: REPORT_COLUMN_WIDTH,
      actions: true,
      enableSort: true,
    },
    {
      field: "liteTestReport",
      headerName: "Lite Test Report",
      customHeader: (
        <ReportColumnHeader
          abbreviation="L.T.R."
          reportName="Lite Test Report"
        />
      ),
      columnMinWidth: REPORT_COLUMN_WIDTH,
      columnMaxWidth: REPORT_COLUMN_WIDTH,
      actions: true,
      enableSort: true,
    },
    {
      field: "routineTestCertificate",
      headerName: "Routine Test Certificate",
      customHeader: (
        <ReportColumnHeader
          abbreviation="R.T.C."
          reportName="Routine Test Certificate"
        />
      ),
      columnMinWidth: REPORT_COLUMN_WIDTH,
      columnMaxWidth: REPORT_COLUMN_WIDTH,
      actions: true,
      enableSort: true,
    },
  ]

  const rows = factoryReports.map((factoryReport, index) => {
    const testReport =
      factoryReport.status === "Found"
        ? getReportByType(factoryReport, "TestReport")
        : undefined
    const liteTestReport =
      factoryReport.status === "Found"
        ? getReportByType(factoryReport, "LiteTestReport")
        : undefined
    const routineTestCertificate =
      factoryReport.status === "Found"
        ? getReportByType(factoryReport, "RoutineTestCertificate")
        : undefined

    return {
      id: `${factoryReport.serialNumber ?? "factory-report"}-${index}`,
      serialNumber: factoryReport.serialNumber ?? "",
      partNumber: factoryReport.partNumber ?? "",
      deviceName: factoryReport.deviceName ?? "",
      status: factoryReport.status ?? "",
      testReport: (
        <ReportDownloadButton
          report={testReport}
          reportName="Test Report"
          text={getReportSortValue(testReport)}
        />
      ),
      liteTestReport: (
        <ReportDownloadButton
          report={liteTestReport}
          reportName="Lite Test Report"
          text={getReportSortValue(liteTestReport)}
        />
      ),
      routineTestCertificate: (
        <ReportDownloadButton
          report={routineTestCertificate}
          reportName="Routine Test Certificate"
          text={getReportSortValue(routineTestCertificate)}
        />
      ),
    }
  })

  const serialNumberDescription =
    serialNumbers.length === 1
      ? "1 valid serial number"
      : `${serialNumbers.length} valid serial numbers`

  return (
    <EntityTab
      rows={rows}
      cols={columns}
      isLoading={isRetrievingFactoryReports}
      toolbar={
        <div className="factory-report-search-toolbar">
          <div className="factory-report-search-input">
            <MUITextField
              id="factory-report-serial-numbers"
              label="Serial numbers"
              description={serialNumberDescription}
              placeholder={SEARCH_PLACEHOLDER}
              value={searchText}
              onChange={setSearchText}
              onKeyDown={onKeyDown}
              onClickClear={clearSearchText}
              multiline
              rows={3}
              allowedChars="all"
            />
          </div>
          <MUIButton
            label="Search"
            iconName="icon_abb_dias icon-abb_personal-mangement"
            variant="primary"
            size={32}
            isLoading={isRetrievingFactoryReports}
            disabled={serialNumbers.length === 0}
            onClickButton={retrieveFactoryReports}
          />
        </div>
      }
    />
  )
}
